import invariant from 'invariant';
import { useEffect, useRef, useState } from 'react';

import { useTabContext } from 'containers/TabContext';
import waitForElement from 'utils/waitForElement';

import { ModalPropsForType } from './ModalPropsForType';
import { ModalType } from './ModalType';
import { modalComponentByModalType } from './modalComponentByModalType';

type Props<T extends ModalType> = {
  type: ModalType;
  modalProps: ModalPropsForType<T>;
};

export function ModalByType<T extends ModalType>({
  type,
  modalProps,
}: Props<T>) {
  const [modalContext, setModalContext] = useState<HTMLElement | null>(null);
  const [focused, setFocused] = useState(false);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const isTabbing = useTabContext();

  useEffect(() => {
    if (focused) return;

    setFocused(true);

    const modalContextElement = document.querySelector(
      '*:focus',
    ) as HTMLElement | null;
    setModalContext(modalContextElement);

    // TODO: Fix this the next time the file is edited.
    // @ts-expect-error TS(2345): Argument of type '() => Element | undefined' is no... Remove this comment to see the full error message
    waitForElement(() => {
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (modalContainerRef && modalContainerRef.current) {
        const container = modalContainerRef.current;
        const els = Array.from(
          container.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        );

        if (isTabbing) {
          return (
            els.find((el) => el.matches(':not([data-modal-focus="false"])')) ||
            els[0]
          );
        }

        const firstFocusableElement =
          els.find((el) => el.matches('[data-modal-focus="true"]')) ||
          els.find((el) => el.matches(':not([data-modal-focus="false"])')) ||
          els[0];
        return firstFocusableElement;
      }
    }, 200)
      .then((el) => {
        el.focus();
      })
      .catch(() => {
        // do nothing
      });
  }, [type, modalContext, isTabbing, focused]);

  const ModalComponent = modalComponentByModalType[type];
  invariant(ModalComponent, `Invalid modal type: ${type}`);

  return (
    <div ref={modalContainerRef}>
      {/* @ts-expect-error TS(2604): JSX element type 'ModalComponent' does not have an... Remove this comment to see the full error message */}
      <ModalComponent
        // We need to spread here, no other choice unless we stop showing modals
        // using Redux
        //
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...modalProps}
      />
    </div>
  );
}
